import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useTranslationStore } from '@shared/lib/translation/useTranslationStore';

export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALES = ['en', 'es'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
const STORAGE_KEY = 'jema-manager-locale';

const splitPathAndSuffix = (input: string): { pathname: string; suffix: string } => {
  const match = input.match(/^([^?#]*)(.*)$/);
  return {
    pathname: match?.[1] || input,
    suffix: match?.[2] || '',
  };
};

export const isSupportedLocale = (value: string | null | undefined): value is SupportedLocale =>
  Boolean(value && SUPPORTED_LOCALES.includes(value as SupportedLocale));

export const getLocaleFromPathname = (pathname: string): SupportedLocale | null => {
  const [firstSegment] = pathname.split('/').filter(Boolean);
  return isSupportedLocale(firstSegment) ? firstSegment : null;
};

export const stripLocaleFromPathname = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  const [firstSegment, ...restSegments] = segments;
  const shouldStrip =
    isSupportedLocale(firstSegment) || Boolean(firstSegment && /^[a-z]{2}$/i.test(firstSegment));

  if (!shouldStrip) {
    return pathname || '/';
  }

  return restSegments.length > 0 ? `/${restSegments.join('/')}` : '/';
};

export const localizePath = (input: string, locale: SupportedLocale): string => {
  const { pathname, suffix } = splitPathAndSuffix(input);
  const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const strippedPathname = stripLocaleFromPathname(normalizedPathname);
  const localizedPathname =
    strippedPathname === '/' ? `/${locale}` : `/${locale}${strippedPathname}`;

  return `${localizedPathname}${suffix}`;
};

type I18nProviderProps = {
  children: ReactNode;
  locale?: SupportedLocale;
};

type I18nContextValue = {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
};

// Keep a single context identity across Vite HMR so providers/consumers
// from remounted module instances still match (avoids "useLocale must be
// used within AppI18nProvider" after locale catalog reloads).
const I18N_CONTEXT_KEY = '__jemaManagerI18nContext__';
const I18N_LOADED_KEY = '__jemaManagerI18nHasLoaded__';

type I18nHmrGlobals = typeof globalThis & {
  [I18N_CONTEXT_KEY]?: ReturnType<typeof createContext<I18nContextValue | undefined>>;
  [I18N_LOADED_KEY]?: Set<SupportedLocale>;
};

const i18nGlobals = globalThis as I18nHmrGlobals;

const I18nContext =
  i18nGlobals[I18N_CONTEXT_KEY] ?? createContext<I18nContextValue | undefined>(undefined);
i18nGlobals[I18N_CONTEXT_KEY] = I18nContext;

const hasLoaded = i18nGlobals[I18N_LOADED_KEY] ?? new Set<SupportedLocale>();
i18nGlobals[I18N_LOADED_KEY] = hasLoaded;

const localeLoaders: Record<SupportedLocale, () => Promise<{ messages: Record<string, string> }>> =
  {
    en: () => import('@/locales/en/messages.mjs'),
    es: () => import('@/locales/es/messages.mjs'),
  };

const getBrowserLocale = (): SupportedLocale | null => {
  if (typeof navigator === 'undefined') return null;
  const lang = navigator.language?.split('-')[0]?.toLowerCase();
  if (isSupportedLocale(lang)) {
    return lang as SupportedLocale;
  }
  return null;
};

export const resolvePreferredLocale = (pathname?: string): SupportedLocale => {
  const pathLocale = pathname ? getLocaleFromPathname(pathname) : null;
  if (pathLocale) {
    return pathLocale;
  }

  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isSupportedLocale(stored)) {
      return stored;
    }
  }

  return getBrowserLocale() ?? DEFAULT_LOCALE;
};

const loadLocaleCatalog = async (locale: SupportedLocale) => {
  if (hasLoaded.has(locale)) return;
  const loader = localeLoaders[locale];
  const catalog = await loader();
  i18n.load(locale, catalog.messages);
  hasLoaded.add(locale);
};

const activateLocale = (locale: SupportedLocale) => {
  i18n.activate(locale);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
};

const recreateTreeForLocale = (node: ReactNode): ReactNode =>
  Children.map(node, child => {
    if (!isValidElement(child)) {
      return child;
    }

    const childProps = child.props as { children?: ReactNode };

    return cloneElement(
      child,
      undefined,
      childProps.children ? recreateTreeForLocale(childProps.children) : childProps.children
    );
  });

export const AppI18nProvider = ({ children, locale: initialLocale }: I18nProviderProps) => {
  const [locale, setLocaleState] = useState<SupportedLocale>(
    () =>
      initialLocale ??
      resolvePreferredLocale(typeof window !== 'undefined' ? window.location.pathname : undefined)
  );
  const [isLocaleReady, setIsLocaleReady] = useState(false);
  const [renderVersion, setRenderVersion] = useState(0);

  useEffect(() => {
    let active = true;

    const run = async () => {
      await loadLocaleCatalog(locale);
      activateLocale(locale);

      // Warm alternate locale catalogs in background to keep language switch instant.
      const warmPromises = SUPPORTED_LOCALES.filter(candidate => candidate !== locale).map(
        candidate => loadLocaleCatalog(candidate)
      );
      void Promise.allSettled(warmPromises);

      if (!active) return;
      // Force a React pass after each activation so nodes that only call t()
      // recompute against the newly active catalog without a full page reload.
      setRenderVersion(current => current + 1);
      setIsLocaleReady(true);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, locale);
      }
    };

    void run();

    return () => {
      active = false;
    };
  }, [locale]);

  const setLocale = useCallback(
    (next: SupportedLocale) => {
      if (next === locale) return;
      // If the catalog is already warm, activate immediately to avoid
      // a transient frame where context locale changed but Lingui has not.
      if (hasLoaded.has(next)) {
        activateLocale(next);
      }
      setLocaleState(next);
      useTranslationStore.getState().setTargetLocale(next);
    },
    [locale]
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
    }),
    [locale, setLocale]
  );

  const renderedChildren = useMemo(
    () => recreateTreeForLocale(children),
    [children, locale, renderVersion]
  );

  return (
    <I18nContext.Provider value={value}>
      <I18nProvider i18n={i18n}>{isLocaleReady ? renderedChildren : null}</I18nProvider>
    </I18nContext.Provider>
  );
};

export const useLocale = (): I18nContextValue => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useLocale must be used within AppI18nProvider');
  }
  return ctx;
};
